import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Guard duplicate verification calls in dev StrictMode/re-mount scenarios.
const verifyTokenStatus = new Map();

export default function EmailVerification() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setState('error');
        setErrorMsg(t('emailVerification.error.missingToken'));
        return;
      }

      const currentStatus = verifyTokenStatus.get(token);
      if (currentStatus === 'pending' || currentStatus === 'done') {
        return;
      }
      verifyTokenStatus.set(token, 'pending');

      try {
        const response = await fetch('/api/auth/verify-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok) {
          verifyTokenStatus.delete(token);
          setState('error');
          setErrorMsg(data.detail || t('emailVerification.error.verifyFailed'));
          return;
        }

        // Success - mark as verified
        verifyTokenStatus.set(token, 'done');
        setState('success');

        // Redirect to login after 2 seconds
        // Note: Any existing session will be automatically cleared by server
        setTimeout(() => {
          // Force page refresh to clear any cached auth context
          window.location.href = '/login';
        }, 2000);
      } catch (err) {
        console.error('Verification error:', err);
        verifyTokenStatus.delete(token);
        setState('error');
        setErrorMsg(t('resend.error.generic'));
      }
    };

    verifyEmail();
  }, [searchParams, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md">
        {/* Loading State */}
        {state === 'loading' && (
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full border-4 border-blue-200 flex items-center justify-center bg-white">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                </div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('emailVerification.loading.title')}</h1>
            <p className="text-slate-600">{t('emailVerification.loading.subtitle')}</p>
          </div>
        )}

        {/* Success State */}
        {state === 'success' && (
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full border-4 border-green-200 flex items-center justify-center bg-white">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('emailVerification.success.title')}</h1>
            <p className="text-slate-600 mb-6">{t('emailVerification.success.desc')}</p>
            <p className="text-sm text-slate-500">{t('emailVerification.success.redirecting')}</p>
          </div>
        )}

        {/* Error State */}
        {state === 'error' && (
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full border-4 border-red-200 flex items-center justify-center bg-white">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('emailVerification.error.title')}</h1>
            <p className="text-slate-600 mb-6">{errorMsg}</p>

            {/* Resend Email Option */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-left">
                  <p className="font-medium text-blue-900 mb-2">{t('emailVerification.error.resendTitle')}</p>
                  <p className="text-blue-700 mb-4">{t('emailVerification.error.resendDesc')}</p>
                  <button
                    onClick={() => navigate('/resend-verification', { replace: true })}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm underline"
                  >
                    {t('emailVerification.error.resendBtn')}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition"
              >
                {t('emailVerification.error.backToLogin')}
              </button>
              <button
                onClick={() => navigate('/register', { replace: true })}
                className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition"
              >
                {t('emailVerification.error.backToRegister')}
              </button>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 text-center text-sm text-slate-600">
          <p>{t('emailVerification.help.prefix')} <a href="mailto:support@veriksa.id" className="text-blue-600 hover:underline">{t('emailVerification.help.link')}</a></p>
        </div>
      </div>
    </div>
  );
}
